import { z } from "zod";
import { EKYCStatus } from "../kyc";
import { schemes } from "../schema-validation";
import { EPartnerInformationSection } from "./EPartnerInformationSection";

export const partnerInformationSectionSchema = z.object({
  name: z.nativeEnum(EPartnerInformationSection),
});

export const partnerAdditionalOwnerSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  ssn: z.string(),
  dob: z.string(),
  address: z.string(),
  addressLine2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  ownershipPercentage: z.number().int().min(0).max(100),
  jobTitle: z.string(),
  email: z.string(),
  kycStatus: z.nativeEnum(EKYCStatus).nullable().optional(),
});

export const partnerShareableLinksTokenPayloadSchema = z.object({
  whiteLabelID: z.string(),
  isShareableLink: z.literal(true),
  sections: z.array(partnerInformationSectionSchema),
  testMode: z.boolean(),
  existingAdditionalOwner: partnerAdditionalOwnerSchema.optional(),
  goToStep: z.string().optional(),
});

export const partnerInformationPartnerSchema = z.object({
  white_label_id: schemes.partnerID(),
  business_name: z.string(),
  business_address: z.string(),
  business_address_line2: z.string(),
  business_city: z.string(),
  business_state: z.string(),
  business_zip: z.string(),
  business_phone: z.string(),
  business_email: z.string(),
  business_description: z.string(),
  legal_structure: z.string(),
  business_ein: z.string(),
  isMailingAddressDifferent: z.boolean(),
  mail_address: z.string(),
  mail_address_line2: z.string(),
  mail_city: z.string(),
  mail_state: z.string(),
  mail_zip: z.string(),
  isPhysicalAddressDifferent: z.boolean(),
  physical_address: z.string(),
  physical_address_line2: z.string(),
  physical_city: z.string(),
  physical_state: z.string(),
  physical_zip: z.string(),
  dba: z.string(),
  business_owner_first_name: z.string(),
  business_owner_last_name: z.string(),
  ownership_percentage: z.number().int().min(0).max(100),
  owner_email: z.string(),
  owner_address: z.string(),
  owner_addressLine2: z.string(),
  owner_city: z.string(),
  owner_state: z.string(),
  owner_zip: z.string(),
  ssn: z.string(),
  dob: z.string(),
  job_title: z.string(),
  additionalOwners: z.array(partnerAdditionalOwnerSchema),
  electronic_communication_form: z.date().nullish(),
  direct_deposit_form: z.date().nullish(),
  patriot_act_form: z.date().nullish(),
  puzzl_terms_conditions: z.date().nullish(),
  signature: z.string().nullish(),
  business_model_description: z.string().nullish(),
  expected_volume_and_scale: z.string().nullish(),
  payment_and_funds_flow_description: z.string().nullish(),
  sensitive_data_handling: z.string().nullish(),
  questionnaire_attestation: z.boolean().nullish(),
  status: z.string(),
});

export const partnerInformationPayloadSchema = z.object({
  partner: partnerInformationPartnerSchema,
  logoURL: z.string().nullish(),
  tokenPayload: partnerShareableLinksTokenPayloadSchema,
});

export type TPartnerShareableLinksTokenPayload = z.infer<
  typeof partnerShareableLinksTokenPayloadSchema
>;

export type TPartnerShareableLinksSection = z.infer<
  typeof partnerInformationSectionSchema
>;

export type TPartnerInformationPayload = z.infer<
  typeof partnerInformationPayloadSchema
>;

export type TPartnerAdditionalOwner = z.infer<
  typeof partnerAdditionalOwnerSchema
>;
